<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCountdown } from '../parts/useCountdown';
import PostCommentView from './PostCommentView.vue';

const props = defineProps({
  source: { type: Object, required: true },
  // Unix seconds of the latest incoming message (for the 24h window).
  lastIncomingAt: { type: Number, default: 0 },
});

const { t } = useI18n();

const hasWindow = computed(() => props.source.reply_mode === 'window_24h');

const windowEnd = computed(() => {
  if (!hasWindow.value) return null;
  if (props.lastIncomingAt) {
    return new Date((props.lastIncomingAt + 24 * 3600) * 1000).toISOString();
  }
  return props.source.window_expires_at || null;
});

const { parts, label, isExpired } = useCountdown(windowEnd);

const progress = computed(() => {
  if (!parts.value) return 0;
  return Math.max(4, Math.min(100, (parts.value.totalHours / 24) * 100));
});

const isUrgent = computed(() => parts.value && parts.value.totalHours < 3);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      v-if="hasWindow && windowEnd && !isExpired"
      class="flex flex-col gap-2 px-3 py-2.5 rounded-lg border"
      :class="
        isUrgent
          ? 'bg-n-amber-2 border-n-amber-4'
          : 'bg-n-teal-2 border-n-teal-4'
      "
    >
      <div class="flex items-center gap-2 text-xs">
        <span
          class="i-lucide-hourglass size-4 flex-shrink-0"
          :class="isUrgent ? 'text-n-amber-11' : 'text-n-teal-11'"
        />
        <span class="font-medium text-n-slate-12">
          {{ t('MULTICHAT.SOURCE.DIRECT.WINDOW_OPEN') }}
        </span>
        <span class="flex-1" />
        <span
          class="font-semibold tabular-nums"
          :class="isUrgent ? 'text-n-amber-11' : 'text-n-teal-11'"
        >
          {{ t('MULTICHAT.SOURCE.COUNTDOWN.LEFT', { time: label }) }}
        </span>
      </div>
      <div class="h-1 rounded-full bg-n-alpha-2 overflow-hidden">
        <div
          class="h-full rounded-full transition-all"
          :class="isUrgent ? 'bg-n-amber-9' : 'bg-n-teal-9'"
          :style="{ width: `${progress}%` }"
        />
      </div>
    </div>
    <div
      v-else-if="hasWindow && isExpired"
      class="flex items-start gap-2 px-3 py-2.5 rounded-lg border bg-n-ruby-2 border-n-ruby-4 text-xs"
    >
      <span class="i-lucide-lock size-4 flex-shrink-0 text-n-ruby-11 mt-px" />
      <span class="text-n-ruby-11 font-medium">
        {{ t('MULTICHAT.SOURCE.DIRECT.WINDOW_EXPIRED') }}
      </span>
    </div>
    <div v-else class="flex items-center gap-2 text-xs text-n-slate-11">
      <span class="i-lucide-lock-keyhole size-3.5" />
      {{ t('MULTICHAT.SOURCE.DIRECT.PRIVATE') }}
    </div>
    <PostCommentView v-if="source.post" :source="source" />
  </div>
</template>
